/**
 * Object comparison and diff utilities
 *
 * Provides functions for deep comparison and identifying changes between objects,
 * useful for form change detection, optimistic updates, and audit trails.
 */

import { OBJECT_DIFF } from "./constants";

export interface DiffOptions {
  /**
   * Whether to include fields that were removed from the updated object
   * @default OBJECT_DIFF.DEFAULT_INCLUDE_REMOVALS (false)
   */
  includeRemovals?: boolean;

  /**
   * Custom comparison function for specific types
   * Return true if values should be considered equal
   * Note: Comparator receives individual values, not the entire objects
   */
  customComparator?: (original: unknown, updated: unknown) => boolean | undefined;

  /**
   * Whether array element order is significant when comparing arrays.
   * When true, arrays are compared element-by-element by index. When false,
   * arrays are compared as multisets (equal length and equal member counts,
   * order ignored), and the order-insensitivity propagates into nested arrays.
   *
   * Defaults differ by entry point: `deepEqual` treats order as insignificant
   * (`false`) by default, while `getChangedFields` / `getShallowChanges` keep
   * order significant (`true`) so a reorder still surfaces as a change in the
   * produced patch.
   */
  orderSensitive?: boolean;

  /**
   * Maximum depth for nested object comparison to prevent infinite recursion
   * @default OBJECT_DIFF.DEFAULT_MAX_DEPTH (10)
   */
  maxDepth?: number;

  /**
   * Array of field names to ignore during comparison
   * Useful for ignoring fields like 'id' that are expected to be different
   * @default []
   */
  ignoreFields?: string[];
}

/**
 * Compares two objects and returns only the fields that have changed.
 * Type-safe version that preserves object structure and provides better IntelliSense.
 *
 * @param original - The original object to compare against
 * @param updated - The updated object with potential changes
 * @param options - Configuration options for the comparison
 * @returns Object containing only changed fields, or null if no changes detected
 *
 * @example
 * ```typescript
 * interface User { name: string; age: number; settings: { theme: string } }
 * const original: User = { name: 'John', age: 30, settings: { theme: 'dark' } }
 * const updated: User = { name: 'John', age: 31, settings: { theme: 'light' } }
 *
 * const changes = getChangedFields(original, updated)
 * // Returns: Partial<User> = { age: 31, settings: { theme: 'light' } }
 * ```
 */
export function getChangedFields(
  original: unknown,
  updated: unknown,
  options: DiffOptions = {}
): Record<string, unknown> | null {
  return getChangedFieldsInternal(original, updated, options, 0);
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

interface DeepEqualOptions {
  orderSensitive: boolean;
}

const arraysEqual = (a: unknown[], b: unknown[], options: DeepEqualOptions): boolean => {
  if (a.length !== b.length) return false;

  if (options.orderSensitive) {
    return a.every((value, index) => fastDeepEqual(value, b[index], options));
  }

  const matched = new Array<boolean>(b.length).fill(false);
  return a.every((value) => {
    const matchIndex = b.findIndex((candidate, index) => !matched[index] && fastDeepEqual(value, candidate, options));
    if (matchIndex === -1) return false;
    matched[matchIndex] = true;
    return true;
  });
};

/**
 * Performs a fast deep equality check for primitive values and simple objects.
 * More reliable than JSON.stringify and handles edge cases better.
 */
function fastDeepEqual(a: unknown, b: unknown, options: DeepEqualOptions): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;

  const typeA = typeof a;
  const typeB = typeof b;
  if (typeA !== typeB) return false;

  // Handle primitive types
  if (typeA !== "object") return false;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    return arraysEqual(a, b, options);
  }

  // Only one is array
  if (Array.isArray(a) || Array.isArray(b)) return false;

  // Handle objects
  if (!isRecord(a) || !isRecord(b)) return false;
  if (a.constructor !== b.constructor) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!fastDeepEqual(a[key], b[key], options)) return false;
  }

  return true;
}

function getChangedFieldsInternal(
  original: unknown,
  updated: unknown,
  options: DiffOptions,
  depth: number
): Record<string, unknown> | null {
  const {
    includeRemovals = OBJECT_DIFF.DEFAULT_INCLUDE_REMOVALS,
    customComparator,
    maxDepth = OBJECT_DIFF.DEFAULT_MAX_DEPTH,
    ignoreFields = [],
    orderSensitive = true,
  } = options;

  // Prevent infinite recursion
  if (depth > maxDepth) {
    return !fastDeepEqual(original, updated, { orderSensitive }) ? { __value: updated } : null;
  }

  // Handle null/undefined inputs
  if (original == null || updated == null) {
    return original !== updated ? { __value: updated } : null;
  }

  // Use custom comparator if provided
  if (customComparator) {
    const customResult = customComparator(original, updated);
    if (customResult !== undefined) {
      return customResult ? null : { __value: updated };
    }
  }

  // Handle non-record types (primitives, arrays, dates, etc.)
  if (
    !isRecord(original) ||
    !isRecord(updated) ||
    Array.isArray(original) ||
    Array.isArray(updated) ||
    original instanceof Date ||
    updated instanceof Date
  ) {
    return !fastDeepEqual(original, updated, { orderSensitive }) ? { __value: updated } : null;
  }

  const changes: Record<string, unknown> = {};
  let hasChanges = false;

  // Get all keys from both objects
  const originalKeys = new Set(Object.keys(original));
  const updatedKeys = new Set(Object.keys(updated));
  const allKeys = new Set([...originalKeys, ...updatedKeys]);

  for (const key of allKeys) {
    // Skip ignored fields
    if (ignoreFields.includes(key)) {
      continue;
    }

    const originalValue = original[key];
    const updatedValue = updated[key];

    // Handle removed fields
    if (updatedKeys.has(key) && !originalKeys.has(key)) {
      // New field added
      changes[key] = updatedValue;
      hasChanges = true;
    } else if (originalKeys.has(key) && !updatedKeys.has(key)) {
      // Field removed
      if (includeRemovals) {
        changes[key] = undefined;
        hasChanges = true;
      }
    } else {
      // Field exists in both - check for changes
      const fieldChanges = getChangedFieldsInternal(originalValue, updatedValue, options, depth + 1);
      if (fieldChanges !== null) {
        // For nested objects that returned actual changes, preserve the structure
        // For primitives/arrays/etc that fallback to { __value: X }, unwrap the value
        if ("__value" in fieldChanges && Object.keys(fieldChanges).length === 1) {
          changes[key] = fieldChanges.__value;
        } else {
          // This is a nested object with specific field changes - preserve the nesting
          changes[key] = fieldChanges;
        }
        hasChanges = true;
      }
    }
  }

  return hasChanges ? changes : null;
}

/**
 * Checks if two objects are deeply equal.
 * Type-safe version that ensures both objects have the same structure.
 *
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @param options - Comparison options
 * @returns true if objects are equal, false otherwise
 *
 * Arrays are compared order-insensitively by default (as multisets); pass
 * `{ orderSensitive: true }` to compare array elements by index instead.
 *
 * @example
 * ```typescript
 * interface Settings { name: string; settings: { theme: string } }
 * const obj1: Settings = { name: 'John', settings: { theme: 'dark' } }
 * const obj2: Settings = { name: 'John', settings: { theme: 'dark' } }
 *
 * const isEqual = deepEqual(obj1, obj2) // Returns: true
 *
 * deepEqual({ tags: ['a', 'b'] }, { tags: ['b', 'a'] })                        // true (order ignored)
 * deepEqual({ tags: ['a', 'b'] }, { tags: ['b', 'a'] }, { orderSensitive: true }) // false
 * ```
 */
export function deepEqual<T>(obj1: T, obj2: T, options: DiffOptions = {}): boolean {
  return getChangedFields(obj1, obj2, { orderSensitive: false, ...options }) === null;
}

/**
 * Creates a shallow copy of an object with only the specified changed fields.
 * Useful for creating patch objects for API updates.
 * Type-safe version that preserves the object structure.
 *
 * @param original - The original object
 * @param updated - The updated object
 * @param options - Comparison options (currently unused but kept for consistency)
 * @returns Object containing only root-level changed fields, or null if no changes
 *
 * @example
 * ```typescript
 * interface User { name: string; age: number; settings: { theme: string; lang: string } }
 * const original: User = { name: 'John', age: 30, settings: { theme: 'dark', lang: 'en' } }
 * const updated: User = { name: 'John', age: 31, settings: { theme: 'light', lang: 'en' } }
 *
 * const patch = getShallowChanges(original, updated)
 * // Returns: Partial<User> = { age: 31, settings: { theme: 'light', lang: 'en' } }
 * ```
 */
export function getShallowChanges(
  original: unknown,
  updated: unknown,
  options: DiffOptions = {}
): Record<string, unknown> | null {
  if (!isRecord(original) || !isRecord(updated)) {
    return original !== updated ? { __value: updated } : null;
  }

  const { ignoreFields = [], orderSensitive = true } = options;
  const changes: Record<string, unknown> = {};
  let hasChanges = false;

  const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)]);

  for (const key of allKeys) {
    // Skip ignored fields
    if (ignoreFields.includes(key)) {
      continue;
    }

    if (!fastDeepEqual(original[key], updated[key], { orderSensitive })) {
      changes[key] = updated[key];
      hasChanges = true;
    }
  }

  return hasChanges ? changes : null;
}
